import React, { useState } from 'react';
import { db } from '../services/firebase';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { isPendenteExpirado } from '../services/agendamentoSlot';

function DetalhesAgendamento({ agendamento, onFechar, onCancelado }) {
  const [cancelando, setCancelando] = useState(false);
  const [confirmandoCancelamento, setConfirmandoCancelamento] = useState(false);
  const [confirmandoAgendamento, setConfirmandoAgendamento] = useState(false);
  const [desbloqueando, setDesbloqueando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  if (!agendamento) return null;

  const isBloqueado = agendamento.status === 'bloqueado';
  const isCancelado = agendamento.status === 'cancelado';
  // Pendente cujo horário já começou: expirado — não pode mais ser confirmado
  const isExpirado  = isPendenteExpirado(agendamento);
  const isPendente  = agendamento.status === 'pendente' && !isExpirado;
  const isManual    = agendamento.tipo === 'manual';

  // Suporta campos novos (clienteNome/clienteTelefone) e legados (cliente/telefone)
  const nomeCliente = agendamento.clienteNome || agendamento.cliente || '—';
  const telCliente  = agendamento.clienteTelefone || agendamento.telefone || '';

  function abrirWhatsApp(mensagem) {
    const numero = telCliente.replace(/\D/g, '');
    if (!numero) return;
    window.open(`https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`, '_blank');
  }

  // Push para o cliente (canal in-app, independente do WhatsApp). Só dispara quando há
  // clienteId — agendamentos manuais (clienteId vazio) e bloqueios não têm destinatário.
  function notificarCliente(titulo, mensagem) {
    if (!agendamento.clienteId) return;
    fetch('/api/notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clienteId: agendamento.clienteId, titulo, mensagem }),
    }).catch(() => {});
  }

  function mensagemConfirmacao() {
    return `Olá ${nomeCliente.split(' ')[0]}! 🎯\nSeu agendamento na *${agendamento.barbearia}* foi confirmado!\n\n📅 ${parseInt(agendamento.dia)} de ${agendamento.mes}\n⏰ ${agendamento.horario}\n✂️ ${agendamento.barbeiro} — ${agendamento.servico}\n\nTe esperamos! 💈`;
  }

  function mensagemCancelamento() {
    return `Olá ${nomeCliente.split(' ')[0]}! Seu agendamento na *${agendamento.barbearia}* para ${parseInt(agendamento.dia)} de ${agendamento.mes} às ${agendamento.horario} foi cancelado. Entre em contato para reagendar. 🙏`;
  }

  const origemLabel = isBloqueado ? 'Bloqueado' : isManual ? 'Agendamento manual' : 'App BLADE';
  const origemCor   = isBloqueado ? '#c9a84c'   : isManual ? '#7bc47b'           : '#378ADD';
  const origemBg    = isBloqueado ? '#2a1a0a'   : isManual ? '#1a2a1a'           : '#0a1a2a';
  const origemBorda = isBloqueado ? '#4a3a1a'   : isManual ? '#2d4a2d'           : '#1a3a5a';

  async function handleDesbloquear() {
    setDesbloqueando(true);
    try {
      await deleteDoc(doc(db, 'agendamentos', agendamento.id));
      if (onCancelado) onCancelado();
      onFechar();
    } catch (e) {
      console.error('Erro ao desbloquear:', e);
      setDesbloqueando(false);
    }
  }

  async function handleCancelar(avisar = false) {
    setCancelando(true);
    try {
      await updateDoc(doc(db, 'agendamentos', agendamento.id), { status: 'cancelado' });
      setConfirmandoCancelamento(false);
      notificarCliente('Agendamento cancelado', `${agendamento.barbearia} cancelou seu horário de ${parseInt(agendamento.dia)} de ${agendamento.mes} às ${agendamento.horario}.`);
      if (avisar && telCliente) abrirWhatsApp(mensagemCancelamento());
      if (onCancelado) onCancelado();
      onFechar();
    } catch (e) {
      console.error('Erro ao cancelar:', e);
      setCancelando(false);
    }
  }

  async function handleConfirmar(avisar = false) {
    // Não confirma agendamento cujo horário já começou (expirado)
    if (isExpirado) { setConfirmandoAgendamento(false); return; }
    setConfirmando(true);
    try {
      await updateDoc(doc(db, 'agendamentos', agendamento.id), { status: 'confirmado' });
      notificarCliente('Agendamento confirmado! ✅', `${agendamento.barbearia} confirmou seu horário de ${parseInt(agendamento.dia)} de ${agendamento.mes} às ${agendamento.horario}.`);
      if (avisar && telCliente) abrirWhatsApp(mensagemConfirmacao());
      if (onCancelado) onCancelado();
      onFechar();
    } catch (e) {
      console.error('Erro ao confirmar:', e);
      setConfirmando(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      zIndex: 999, fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{
        width: '100%', maxWidth: '480px',
        backgroundColor: '#181818', borderRadius: '20px 20px 0 0',
        padding: '24px', borderTop: '0.5px solid #252525'
      }}>

        {/* Handle */}
        <div style={{ width: '40px', height: '4px', backgroundColor: '#333', borderRadius: '2px', margin: '0 auto 20px' }} />

        {/* Cabeçalho */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#e8e8e8', margin: 0 }}>
            Detalhes do agendamento
          </h3>
          <span onClick={onFechar} style={{ fontSize: '20px', color: '#aeaeae', cursor: 'pointer' }}>×</span>
        </div>

        {/* Badge de origem */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          backgroundColor: origemBg, border: `0.5px solid ${origemBorda}`,
          borderRadius: '20px', padding: '5px 12px', marginBottom: '16px'
        }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: origemCor }} />
          <span style={{ fontSize: '11px', color: origemCor, fontWeight: '600' }}>{origemLabel}</span>
        </div>

        {/* Horário e barbeiro */}
        <div style={{
          backgroundColor: '#111', border: '0.5px solid #252525',
          borderRadius: '12px', padding: '14px', marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '11px', color: '#aeaeae' }}>Horário</span>
            <span style={{ fontSize: '13px', color: '#e8e8e8', fontWeight: '600' }}>{agendamento.horario}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '11px', color: '#aeaeae' }}>Barbeiro</span>
            <span style={{ fontSize: '13px', color: '#e8e8e8' }}>{agendamento.barbeiro}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '11px', color: '#aeaeae' }}>Status</span>
            <span style={{ fontSize: '12px', fontWeight: '600',
              color: agendamento.status === 'confirmado' ? '#7bc47b'
                   : agendamento.status === 'cancelado'  ? '#e05555'
                   : agendamento.status === 'bloqueado'  ? '#c9a84c'
                   : isExpirado ? '#9c9c9c' : '#e8a84c' }}>
              {agendamento.status === 'confirmado' ? 'Confirmado'
             : agendamento.status === 'cancelado'  ? 'Cancelado'
             : agendamento.status === 'bloqueado'  ? 'Bloqueado'
             : isExpirado ? 'Expirado' : 'Pendente'}
            </span>
          </div>
        </div>

        {/* Dados do cliente */}
        {!isBloqueado && (
          <div style={{
            backgroundColor: '#111', border: '0.5px solid #252525',
            borderRadius: '12px', padding: '14px', marginBottom: '12px'
          }}>
            <div style={{ fontSize: '10px', color: '#aeaeae', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '10px' }}>
              Cliente
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: telCliente ? '8px' : 0 }}>
              <span style={{ fontSize: '11px', color: '#aeaeae' }}>Nome</span>
              <span style={{ fontSize: '13px', color: '#e8e8e8' }}>{nomeCliente}</span>
            </div>
            {telCliente && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: '#aeaeae' }}>Telefone</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <a href={`tel:${telCliente}`} style={{ fontSize: '13px', color: '#7bc47b', textDecoration: 'none' }}>
                    {telCliente}
                  </a>
                  <a
                    href={`https://wa.me/${telCliente.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: '28px', height: '28px', borderRadius: '50%',
                      backgroundColor: '#1a2d1a', border: '0.5px solid #25a244',
                      fontSize: '15px', textDecoration: 'none', flexShrink: 0
                    }}
                    title="Abrir no WhatsApp"
                  >💬</a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Serviço e valor */}
        {!isBloqueado && (
          <div style={{
            backgroundColor: '#111', border: '0.5px solid #252525',
            borderRadius: '12px', padding: '14px', marginBottom: '16px'
          }}>
            <div style={{ fontSize: '10px', color: '#aeaeae', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '10px' }}>
              Serviço
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '11px', color: '#aeaeae' }}>Serviço</span>
              <span style={{ fontSize: '13px', color: '#e8e8e8' }}>{agendamento.servico || '—'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '0.5px solid #1e1e1e' }}>
              <span style={{ fontSize: '11px', color: '#aeaeae' }}>Valor</span>
              <span style={{ fontSize: '15px', color: '#c9a84c', fontWeight: '600' }}>{agendamento.preco || '—'}</span>
            </div>
          </div>
        )}

        {/* Bloqueio */}
        {isBloqueado && (
          <div style={{
            backgroundColor: '#1a1209', border: '0.5px solid #4a3a1a',
            borderRadius: '12px', padding: '14px', marginBottom: '16px',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '13px', color: '#c9a84c', margin: 0, lineHeight: '1.6' }}>
              Este horário está bloqueado e não aparece como disponível para os clientes.
            </p>
          </div>
        )}

        {/* Modal de confirmação de agendamento */}
        {confirmandoAgendamento && (
          <div style={{
            backgroundColor: '#141f14', border: '0.5px solid #2d4a2d',
            borderRadius: '14px', padding: '16px', marginBottom: '12px', textAlign: 'center'
          }}>
            <p style={{ fontSize: '13px', color: '#ccc', margin: '0 0 14px', lineHeight: '1.6' }}>
              Confirmar o agendamento de <strong style={{ color: '#e8e8e8' }}>{nomeCliente}</strong> às{' '}
              <strong style={{ color: '#e8e8e8' }}>{agendamento.horario}</strong>?
            </p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: telCliente ? '8px' : 0 }}>
              <button
                onClick={() => setConfirmandoAgendamento(false)}
                style={{
                  flex: 1, backgroundColor: '#252525', color: '#aeaeae',
                  border: '0.5px solid #333', borderRadius: '10px',
                  padding: '11px', fontSize: '13px', fontWeight: '600',
                  cursor: 'pointer', fontFamily: 'Arial, sans-serif'
                }}
              >
                Voltar
              </button>
              <button
                onClick={() => handleConfirmar(false)}
                disabled={confirmando}
                style={{
                  flex: 1, backgroundColor: '#1a2a1a', color: '#7bc47b',
                  border: '0.5px solid #2d4a2d', borderRadius: '10px',
                  padding: '11px', fontSize: '13px', fontWeight: '600',
                  cursor: confirmando ? 'not-allowed' : 'pointer',
                  fontFamily: 'Arial, sans-serif', opacity: confirmando ? 0.6 : 1
                }}
              >
                {confirmando ? 'Confirmando...' : 'Confirmar'}
              </button>
            </div>
            {telCliente && (
              <button
                onClick={() => handleConfirmar(true)}
                disabled={confirmando}
                style={{
                  width: '100%', backgroundColor: '#0f1a0f', color: '#7bc47b',
                  border: '0.5px solid #1a3a1a', borderRadius: '10px',
                  padding: '11px', fontSize: '13px', fontWeight: '600',
                  cursor: confirmando ? 'not-allowed' : 'pointer',
                  fontFamily: 'Arial, sans-serif', opacity: confirmando ? 0.6 : 1
                }}
              >
                💬 Confirmar e avisar no WhatsApp
              </button>
            )}
          </div>
        )}

        {/* Modal de confirmação de cancelamento */}
        {confirmandoCancelamento && (
          <div style={{
            backgroundColor: '#1a1a1a', border: '0.5px solid #4a1a1a',
            borderRadius: '14px', padding: '16px', marginBottom: '12px', textAlign: 'center'
          }}>
            <p style={{ fontSize: '13px', color: '#ccc', margin: '0 0 14px', lineHeight: '1.6' }}>
              Cancelar o agendamento de <strong style={{ color: '#e8e8e8' }}>{nomeCliente}</strong> às{' '}
              <strong style={{ color: '#e8e8e8' }}>{agendamento.horario}</strong>?<br/>
              <span style={{ fontSize: '11px', color: '#8f8f8f' }}>O horário será liberado automaticamente.</span>
            </p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: telCliente ? '8px' : 0 }}>
              <button
                onClick={() => setConfirmandoCancelamento(false)}
                style={{
                  flex: 1, backgroundColor: '#252525', color: '#aeaeae',
                  border: '0.5px solid #333', borderRadius: '10px',
                  padding: '11px', fontSize: '13px', fontWeight: '600',
                  cursor: 'pointer', fontFamily: 'Arial, sans-serif'
                }}
              >
                Voltar
              </button>
              <button
                onClick={() => handleCancelar(false)}
                disabled={cancelando}
                style={{
                  flex: 1, backgroundColor: '#2a1414', color: '#e05555',
                  border: '0.5px solid #4a1a1a', borderRadius: '10px',
                  padding: '11px', fontSize: '13px', fontWeight: '600',
                  cursor: cancelando ? 'not-allowed' : 'pointer',
                  fontFamily: 'Arial, sans-serif', opacity: cancelando ? 0.6 : 1
                }}
              >
                {cancelando ? 'Cancelando...' : 'Cancelar'}
              </button>
            </div>
            {telCliente && (
              <button
                onClick={() => handleCancelar(true)}
                disabled={cancelando}
                style={{
                  width: '100%', backgroundColor: '#1a0f0f', color: '#e05555',
                  border: '0.5px solid #4a1a1a', borderRadius: '10px',
                  padding: '11px', fontSize: '13px', fontWeight: '600',
                  cursor: cancelando ? 'not-allowed' : 'pointer',
                  fontFamily: 'Arial, sans-serif', opacity: cancelando ? 0.6 : 1
                }}
              >
                💬 Cancelar e avisar no WhatsApp
              </button>
            )}
          </div>
        )}

        {/* Botões de ação */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Aviso de pendente expirado — não dá mais para confirmar */}
          {isExpirado && !confirmandoCancelamento && (
            <div style={{
              width: '100%', backgroundColor: '#161616', color: '#9c9c9c',
              border: '0.5px solid #2a2a2a', borderRadius: '12px',
              padding: '13px', fontSize: '12px', textAlign: 'center', lineHeight: '1.5'
            }}>
              ⏳ Este pedido expirou — o horário já começou e não pode mais ser confirmado.
            </div>
          )}
          {/* Confirmar agendamento pendente */}
          {isPendente && !confirmandoCancelamento && !confirmandoAgendamento && (
            <button
              onClick={() => setConfirmandoAgendamento(true)}
              style={{
                width: '100%', backgroundColor: '#1a2a1a', color: '#7bc47b',
                border: '0.5px solid #2d4a2d', borderRadius: '12px',
                padding: '13px', fontSize: '13px', fontWeight: '600',
                cursor: 'pointer', fontFamily: 'Arial, sans-serif'
              }}
            >
              ✓ Confirmar agendamento
            </button>
          )}
          {/* Botão desbloquear — só para bloqueios */}
          {isBloqueado && (
            <button
              onClick={handleDesbloquear}
              disabled={desbloqueando}
              style={{
                width: '100%', backgroundColor: '#1a2a1a', color: '#7bc47b',
                border: '0.5px solid #2d4a2d', borderRadius: '12px',
                padding: '13px', fontSize: '13px', fontWeight: '600',
                cursor: desbloqueando ? 'not-allowed' : 'pointer',
                fontFamily: 'Arial, sans-serif', opacity: desbloqueando ? 0.6 : 1
              }}
            >
              {desbloqueando ? 'Desbloqueando...' : 'Desbloquear horário'}
            </button>
          )}
          {/* Botão cancelar — só para agendamentos ativos */}
          {!isBloqueado && !isCancelado && !confirmandoCancelamento && !confirmandoAgendamento && (
            <button
              onClick={() => setConfirmandoCancelamento(true)}
              style={{
                width: '100%', backgroundColor: '#1a0a0a', color: '#e05555',
                border: '0.5px solid #4a1a1a', borderRadius: '12px',
                padding: '13px', fontSize: '13px', fontWeight: '600',
                cursor: 'pointer', fontFamily: 'Arial, sans-serif'
              }}
            >
              Cancelar agendamento
            </button>
          )}

          <button onClick={onFechar} style={{
            width: '100%', backgroundColor: '#1a1a1a',
            color: '#b6b6b6', border: '0.5px solid #333',
            borderRadius: '12px', padding: '13px', fontSize: '14px',
            cursor: 'pointer', fontFamily: 'Arial, sans-serif'
          }}>
            Fechar
          </button>
        </div>

      </div>
    </div>
  );
}

export default DetalhesAgendamento;
